import React from 'react';

import { useReducedMotion } from '../../hooks/useReducedMotion';
import { ART_STYLE } from '../../theme/tokens';

export interface SplitArrival {
  railRef: React.RefObject<HTMLElement>;
  dividerRef: React.RefObject<HTMLDivElement>;
  samplesRef: React.RefObject<HTMLElement>;
}

/**
 * The studio's move from a draft (its ingredients in the sheet, or waiting to be opened, before the first extraction) to
 * the ingredients beside the samples, animated when it happens on screen: the style's first definition landing with
 * the sheet closed. The rail glides from the centre of the card to its column (FLIP: measured where it now sits, then
 * slid there from the centre at the same width), the divider fades in, and the samples slide and fade in beside it a
 * beat behind. Opening a style that already has a definition, or switching styles, shows the split as it is; nothing
 * moves under reduced motion. With the sheet open the sheet itself becomes the rail (useIngredientsMorph), and
 * `suppressRef` is up for that commit so the two never run together.
 */
export const useSplitArrival = (
  styleId: string | undefined,
  split: boolean,
  suppressRef?: React.MutableRefObject<boolean>,
): SplitArrival => {
  const reduced = useReducedMotion();
  const railRef = React.useRef<HTMLElement>(null);
  const dividerRef = React.useRef<HTMLDivElement>(null);
  const samplesRef = React.useRef<HTMLElement>(null);
  /** The layout the previous commit showed, so only a draft-to-split change on the same style animates. */
  const lastRef = React.useRef<{ styleId: string | undefined; split: boolean } | null>(null);

  React.useLayoutEffect(() => {
    const last = lastRef.current;
    lastRef.current = { styleId, split };
    if (reduced || suppressRef?.current || !split || !last || last.split || last.styleId !== styleId) return undefined;
    const rail = railRef.current;
    const studio = rail?.parentElement;
    if (!rail || !studio) return undefined;

    // Glide in from the centre of the studio, at the rail's own width.
    const box = studio.getBoundingClientRect();
    const now = rail.getBoundingClientRect();
    const dx = box.left + (box.width - now.width) / 2 - now.left;
    const glides = Math.abs(dx) >= 1;
    const timing = { easing: ART_STYLE.SPLIT_EASE, fill: 'backwards' as const };
    const animations: Animation[] = [];

    if (glides) {
      animations.push(
        rail.animate([{ transform: `translateX(${dx}px)` }, { transform: 'none' }], {
          ...timing,
          duration: ART_STYLE.SPLIT_MS,
        }),
      );
    }
    const divider = dividerRef.current;
    if (divider) {
      animations.push(
        divider.animate([{ opacity: 0 }, { opacity: 1 }], {
          ...timing,
          duration: ART_STYLE.SPLIT_DIVIDER_MS,
          delay: ART_STYLE.SPLIT_DIVIDER_DELAY_MS,
        }),
      );
    }
    const samples = samplesRef.current;
    if (samples) {
      // Stacked (narrow), the rail does not move: the samples rise in below it instead of sliding in beside it.
      const offset = glides
        ? `translateX(${ART_STYLE.SPLIT_SLIDE_PX}px)`
        : `translateY(${ART_STYLE.SPLIT_SLIDE_PX / 2}px)`;
      animations.push(
        samples.animate(
          [
            { opacity: 0, transform: offset },
            { opacity: 1, transform: 'none' },
          ],
          { ...timing, duration: ART_STYLE.SPLIT_SAMPLES_MS, delay: ART_STYLE.SPLIT_SAMPLES_DELAY_MS },
        ),
      );
    }
    return () => animations.forEach((animation) => animation.cancel());
  }, [styleId, split, reduced, suppressRef]);

  return { railRef, dividerRef, samplesRef };
};
